import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home, Route, CalendarDays, Clock, Receipt, ListChecks, MapPinOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { TulsiLogo } from "@/lib/brand";
import SyncPill from "@/offline/SyncPill";
import NotificationBell from "@/components/NotificationBell";
import TrackingChip from "@/components/TrackingChip";
import ShellBackButton from "@/components/ShellBackButton";
import BroadcastPopup from "@/components/BroadcastPopup";
import LocationRequiredPopup from "@/components/LocationRequiredPopup";
import { useFieldTracking } from "@/offline/useFieldTracking";
import { api } from "@/lib/api";

// Hotfix — "Money" hidden from the field bottom nav for everyone except
// SUPER_ADMIN (who doesn't use this mobile shell anyway). User asked to
// pull it for now, re-add it if/when they say it's needed again — just
// restore the `{ to: "/money", ... }` entry below.
const NAV_BASE = [
  { to: "/", label: "Home", icon: Home, testid: "field-nav-home", end: true },
  { to: "/attendance", label: "Attendance", icon: Clock, testid: "field-nav-attendance" },
  { to: "/pjp", label: "Route", icon: Route, testid: "field-nav-route" },
  { to: "/today-beat", label: "Beat", icon: ListChecks, testid: "field-nav-today-beat" },
  { to: "/leave", label: "Leave", icon: CalendarDays, testid: "field-nav-leave" },
];
const NAV_TADA = { to: "/tada", label: "TA/DA", icon: Receipt, testid: "field-nav-tada" };

export default function MobileShell({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const onHome = useLocation().pathname === "/";

  // Location tracking (dense trail while punched in + 5-min heartbeat
  // otherwise) — shared with OfficeShell, see offline/useFieldTracking.js.
  const workflowPerms = user?.workflow_permissions || user?.workflow_perms || [];
  const hasTaDa = workflowPerms.includes("TA_DA");
  const { trail, heartbeat } = useFieldTracking(user);

  // Drop 9a · Item 7 — Sentinel banner REMOVED (reversal of Drop 6/7).
  // POST /visits/start's 409 dedup (Drop 7 Fix B) already prevents the
  // accidental parallel in_progress case the sentinel was masking.

  // Bottom nav — swap Leave for TA/DA when user has TA_DA perm (not distributor).
  // NAV_BASE is now [Home, Attendance, Route, Beat, Leave] (Money removed) —
  // slice(0, 4) drops Leave, matching the pre-existing "swap" behavior.
  const NAV = (hasTaDa && user?.role_code !== "DISTRIBUTOR")
    ? [...NAV_BASE.slice(0, 4), NAV_TADA]
    : NAV_BASE;

  const initials = (user?.name || "?")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen w-full bg-[#f7f5f9] flex justify-center" data-testid="mobile-shell">
      <div className="w-full max-w-[480px] min-h-screen bg-white flex flex-col shadow-[0_0_0_1px_rgba(0,0,0,0.04)] relative">
        <header className="pt-safe px-4 pt-4 pb-3 border-b border-neutral-100 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-20">
          <div className="flex items-center gap-2">
            <ShellBackButton homePath="/" />
            {/* the word mark gives way to the back arrow on inner pages so the header does not overflow */}
            <TulsiLogo size={32} showWord={onHome} />
            <TrackingChip
              active={trail.active}
              netStatus={trail.netStatus}
              queuedCount={trail.queuedCount}
              canTrack={trail.canTrack}
              gpsPermission={trail.gpsPermission}
            />
          </div>
          <div className="flex items-center gap-3">
            <SyncPill dense />
            <NotificationBell />
            <button
              type="button"
              onClick={() => navigate("/more")}
              className="h-9 w-9 rounded-full tulsi-gradient text-white flex items-center justify-center text-[11px] font-bold"
              data-testid="field-user-avatar"
              aria-label="More"
            >
              {initials}
            </button>
          </div>
        </header>

        {heartbeat.blocked && (
          <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2.5 sticky top-[65px] z-10"
               data-testid="location-required-banner">
            <MapPinOff className="h-4 w-4 text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0 text-xs text-amber-800">
              Location is off — start call, punch-in and order actions need it. Enable location to continue.
            </div>
            <button type="button" onClick={heartbeat.requestPermission}
                    className="text-xs font-semibold text-amber-900 underline shrink-0"
                    data-testid="location-required-enable-btn">
              Enable
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto pb-24" data-testid="mobile-main">
          {children}
        </main>

        {/* Drop 9a · Item 7 — sentinel banner block removed. */}

        {/* `fixed` at every width, not `md:absolute` — absolute anchors to
            this shell's own (unbounded, min-h-screen) box, so on a long
            page it sits at the true bottom of the content and scrolls
            away instead of staying pinned like the header's `sticky`
            does. `fixed` stays pinned to the viewport regardless of page
            height; only the horizontal centering needs a md: override. */}
        <nav className="fixed bottom-0 inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[480px] bg-white border-t border-neutral-200 pb-safe z-30" data-testid="field-bottom-nav">
          {/* NAV is always exactly 5 items in both branches above (Home,
              Attendance, Route, Beat, + Leave/TA-DA swap). Note: this must
              stay a literal Tailwind class (`grid-cols-${n}` won't work —
              the JIT compiler can't resolve interpolated class names) — if
              the tab count changes again, update this literal to match. */}
          <div className="grid h-16 grid-cols-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                    isActive ? "text-[#E5197F]" : "text-neutral-500 hover:text-neutral-800"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={`h-5 w-5 ${isActive ? "" : ""}`} />
                    <span>{item.label}</span>
                    {isActive && <span className="absolute -bottom-0 h-0.5 w-8 rounded-full tulsi-gradient" />}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
      {/* unread admin broadcast messages — popup until "OK, I have read it" */}
      <BroadcastPopup />
      <LocationRequiredPopup user={user} />
    </div>
  );
}
